import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {Link, useSearchParams} from 'react-router-dom'
import {KTSVG, initialQueryState, useDebounce} from '../../../../../_metronic/helpers'
import {
  QueryRequestProvider,
  useQueryRequest,
} from '../../../../modules/mytasks/core/QueryRequestProvider'
import {
  QueryResponseProvider,
  useQueryResponseLoading,
} from '../../../../modules/mytasks/core/QueryResponseProvider'
import {ListViewProvider, useListView} from '../../../../modules/mytasks/core/ListViewProvider'
import {DropdownOpenProvider} from '../../../../../_metronic/context/DropdownOpenProvider'
import {Task} from '../../../../modules/mytasks/core/_models'
import {MyTaskTimeTrackingCell} from '../../../../modules/mytasks/components/table/columns/MyTaskTimeTrackingCell'
import {MyTaskStatusCell} from '../../../../modules/mytasks/components/table/columns/MyTaskStatusCell'
import {MyTaskActionsCell} from '../../../../modules/mytasks/components/table/columns/MyTaskActionsCell'

const MAX_ROWS = 6

type Seg = 'abertas' | 'atrasadas' | 'entregues'

const SEGS: {key: Seg; label: string}[] = [
  {key: 'abertas', label: 'Abertas'},
  {key: 'atrasadas', label: 'Atrasadas'},
  {key: 'entregues', label: 'Entregues'},
]

const DashboardMyTasksInner: FC = () => {
  const {state, updateState} = useQueryRequest()
  const {tasks} = useListView() as {tasks: Array<Task & Record<string, any>>}
  const isLoading = useQueryResponseLoading()
  const [searchParams, setSearchParams] = useSearchParams()

  // Abre o modal global de edição da tarefa (App.tsx escuta o param ?taskId).
  const openTask = (id?: number) => {
    if (id == null) return
    const next = new URLSearchParams(searchParams)
    next.set('taskId', String(id))
    setSearchParams(next)
  }

  const [seg, setSeg] = useState<Seg>('abertas')
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 500)
  const didInit = useRef(false)

  // Filtro base do widget: sempre a lista pessoal, ordenada por entrega, 1ª página.
  useEffect(() => {
    updateState({
      ...initialQueryState,
      filter: {
        status: seg === 'entregues' ? 'fechada' : 'aberta',
        only_overdue_tasks: seg === 'atrasadas',
        mode: 'table',
        sort_by_delivery_date: true,
      },
    })
    didInit.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seg])

  useEffect(() => {
    if (!didInit.current) return
    const len = debouncedSearch?.length ?? 0
    if (len === 0 || len >= 3) {
      updateState({...initialQueryState, search: debouncedSearch ?? ''})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const rows = useMemo(() => (Array.isArray(tasks) ? tasks.slice(0, MAX_ROWS) : []), [tasks])

  return (
    <div className='dash-section'>
      <div className='dash-section-head'>
        <h2 className='dash-section-title'>Minhas tarefas</h2>
        <div className='d-flex align-items-center gap-3 flex-wrap'>
          <div className='dash-seg' role='tablist' aria-label='Status das tarefas'>
            {SEGS.map((s) => (
              <button
                key={s.key}
                type='button'
                role='tab'
                aria-selected={seg === s.key}
                className={`dash-seg-btn ${seg === s.key ? 'active' : ''}`}
                onClick={() => setSeg(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className='dash-tasks-search position-relative'>
            <KTSVG
              path='/media/icons/duotune/general/gen021.svg'
              className='svg-icon-2 position-absolute top-50 translate-middle-y ms-3'
            />
            <input
              type='text'
              className='form-control form-control-sm form-control-solid ps-10'
              placeholder='Buscar tarefa'
              aria-label='Buscar tarefa'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {isLoading && rows.length === 0 ? (
        <div className='d-flex align-items-center dash-section-subtitle'>
          <span className='spinner-border spinner-border-sm me-2' role='status' />
          Carregando...
        </div>
      ) : rows.length === 0 ? (
        <p className='dash-empty-text text-center py-6 mb-0'>
          {(state.search ?? '').length > 0
            ? 'Nenhuma tarefa encontrada para essa busca.'
            : seg === 'entregues'
            ? 'Nenhuma tarefa entregue por aqui.'
            : seg === 'atrasadas'
            ? 'Nenhuma tarefa atrasada. Tudo em dia.'
            : 'Nenhuma tarefa aberta atribuída a você.'}
        </p>
      ) : (
        <div className='dash-tasks'>
          <div className='dash-tasks-row dash-tasks-row--head'>
            <span>Tarefa</span>
            <span>Cliente · Projeto</span>
            <span>Jornada</span>
            <span>Status</span>
            <span>Ações</span>
          </div>
          {rows.map((task, idx) => (
            <div className='dash-tasks-row' key={task.id ?? idx}>
              <button
                type='button'
                className='dash-tasks-name'
                title={task.name}
                onClick={() => openTask(task.id)}
              >
                {task.name}
              </button>
              <span
                className='dash-tasks-meta'
                title={[task.clientName, task.project_name].filter(Boolean).join(' · ')}
              >
                {[task.clientName, task.project_name].filter(Boolean).join(' · ')}
              </span>
              <div className='dash-tasks-cell dash-tasks-cell--jornada'>
                <MyTaskTimeTrackingCell
                  sequenceResponsibles={task.sequence_responsibles}
                  users={task.users as any}
                  rowIndex={idx}
                  columnId={'timetracking' as any}
                  task_id={task.id}
                  status={task.status ?? ''}
                  play={task.play ?? ''}
                  time={task.total_time ?? 0}
                  name={task.name ?? ''}
                  updateCellData={() => {}}
                />
              </div>
              <div className='dash-tasks-cell dash-tasks-cell--status'>
                <MyTaskStatusCell
                  status2={{name: task.status, color: task.color}}
                  rowIndex={idx}
                  columnId={'status' as any}
                  task_id={task.id}
                  status={task.status ?? ''}
                  color={task.color ?? ''}
                  updateCellData={() => {}}
                />
              </div>
              <div className='dash-tasks-cell dash-tasks-cell--acoes'>
                <MyTaskActionsCell
                  sequenceResponsibles={task.sequence_responsibles}
                  users={task.users as any}
                  taskId={task.id}
                  name={task.name ?? ''}
                  status={task.status ?? ''}
                  play={task.play ?? ''}
                  subtasksCount={task.subtasks_count}
                  creatorId={task.creator_id}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Só há o que "ver todas" quando existe ao menos uma tarefa na lista. */}
      {rows.length > 0 && (
        <div className='dash-tasks-foot'>
          <Link to='/mytasks' className='dash-tasks-foot-link'>
            Ver todas as tarefas
          </Link>
        </div>
      )}
    </div>
  )
}

/**
 * Widget "Minhas tarefas" do dashboard do colaborador. Reaproveita as células
 * funcionais reais (play/cronômetro, status editável, menu de ações) da tela
 * /mytasks, montando um layout próprio e compacto. Os provedores replicam a pilha
 * mínima que essas células esperam; o modal de edição é global (App.tsx, via ?taskId).
 */
const DashboardMyTasks: FC = () => {
  return (
    <QueryRequestProvider>
      <QueryResponseProvider>
        <ListViewProvider>
          <DropdownOpenProvider>
            <DashboardMyTasksInner />
          </DropdownOpenProvider>
        </ListViewProvider>
      </QueryResponseProvider>
    </QueryRequestProvider>
  )
}

export {DashboardMyTasks}
